import React, { useState, useEffect } from 'react';
import { Coins, Plus, Settings, TrendingUp, RefreshCw, Star } from 'lucide-react';
import axios from 'axios';
import constant from '../constants/constant';

const CoinManagement = () => {
  const [circulation, setCirculation] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [settings, setSettings] = useState({ coin_rate: '100' });
  const [loading, setLoading] = useState(true);

  // Forms
  const [issueForm, setIssueForm] = useState({ seller_id: '', coins_amount: '', rate: '1.0000' });
  const [rateForm, setRateForm] = useState('100');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const circRes = await axios.get(`${constant.API_BASE_URL}/admin/business/coins/circulation`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      const sellerRes = await axios.get(`${constant.API_BASE_URL}/admin/business/sellers`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      const settingsRes = await axios.get(`${constant.API_BASE_URL}/admin/business/system/settings`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });

      if (circRes.data.success) setCirculation(circRes.data.data);
      if (sellerRes.data.success) setSellers(sellerRes.data.data.filter(s => s.status === 'approved'));
      if (settingsRes.data.success) {
        setSettings(settingsRes.data.data);
        setRateForm(settingsRes.data.data.coin_rate || '100');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleIssueCoins = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${constant.API_BASE_URL}/admin/business/coins/issue`, {
        seller_id: issueForm.seller_id,
        coins_amount: parseInt(issueForm.coins_amount),
        rate: parseFloat(issueForm.rate)
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        alert("Coins issued to seller successfully!");
        setIssueForm({ seller_id: '', coins_amount: '', rate: '1.0000' });
        fetchData();
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to issue coins");
    }
  };

  const handleSaveRate = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.put(`${constant.API_BASE_URL}/admin/business/system/settings`, {
        key: 'coin_rate',
        value: rateForm
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        alert("Coin exchange rate updated!");
        fetchData();
      }
    } catch (err) {
      alert("Failed to update rate");
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800">Coin Management</h2>
          <p className="text-slate-500 mt-1">Issue coins to approved sellers, configure application exchange rates, and track global circulation.</p>
        </div>
        <button 
          onClick={fetchData}
          className="p-2.5 bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition active:scale-95 shadow-sm flex items-center justify-center cursor-pointer"
          title="Refresh Circulation"
        >
          <RefreshCw size={20} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Forms column */}
        <div className="space-y-8">
          {/* Issue Coins Form */}
          <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/50">
            <h3 className="text-lg font-bold text-slate-800 mb-4">
              Issue Coins to Seller
            </h3>
            <form onSubmit={handleIssueCoins} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase">Select Approved Seller</label>
                <select
                  required
                  className="w-full mt-1.5 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition"
                  value={issueForm.seller_id}
                  onChange={(e) => setIssueForm({ ...issueForm, seller_id: e.target.value })}
                >
                  <option value="">-- Select Seller --</option>
                  {sellers.map((s) => (
                    <option key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.level})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-400 uppercase">Coins Amount</label>
                <input
                  required
                  type="number"
                  placeholder="e.g. 50000"
                  className="w-full mt-1.5 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition"
                  value={issueForm.coins_amount}
                  onChange={(e) => setIssueForm({ ...issueForm, coins_amount: e.target.value })}
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-400 uppercase">Exchange Rate Modifier (Rate per coin)</label>
                <input
                  required
                  type="text"
                  placeholder="e.g. 1.0000"
                  className="w-full mt-1.5 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition"
                  value={issueForm.rate}
                  onChange={(e) => setIssueForm({ ...issueForm, rate: e.target.value })}
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 bg-pink-600 hover:bg-pink-700 text-white font-bold rounded-xl active:scale-95 transition shadow-lg shadow-pink-200"
              >
                Issue Coins
              </button>
            </form>
          </div>

          {/* Settings / Rates Configuration Form */}
          <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/50">
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2 mb-4">
              <Settings size={20} className="text-slate-700" /> Exchange Rate Settings
            </h3>
            <form onSubmit={handleSaveRate} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase">Global Coin Rate (Coins per 1 USD)</label>
                <input
                  required
                  type="number"
                  className="w-full mt-1.5 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition font-bold text-lg"
                  value={rateForm}
                  onChange={(e) => setRateForm(e.target.value)}
                />
              </div>
              <button
                type="submit"
                className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl active:scale-95 transition"
              >
                Update Exchange Rate
              </button>
            </form>
          </div>
        </div>

        {/* Coin circulation analytics table */}
        <div className="lg:col-span-2 bg-white p-6 rounded-3xl border border-slate-100 shadow-sm overflow-hidden flex flex-col">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-bold text-slate-800">Circulation Audit Trail</h3>
            <span className="text-xs text-slate-400 font-bold uppercase">Last 100 Transactions</span>
          </div>

          {loading ? (
            <div className="p-20 text-center flex-1">
              <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-slate-500">Loading audit trail...</p>
            </div>
          ) : circulation.length === 0 ? (
            <div className="p-20 text-center text-slate-400 flex-1">
              <Coins size={48} className="mx-auto mb-4 opacity-30" />
              <p>No coin movements recorded.</p>
            </div>
          ) : (
            <div className="overflow-auto flex-1 max-h-[350px] scrollbar-thin scrollbar-thumb-slate-200">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50/50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                    <th className="p-4">Action</th>
                    <th className="p-4">Sender</th>
                    <th className="p-4">Receiver</th>
                    <th className="p-4">Coins Amount</th>
                    <th className="p-4">Modifier Rate</th>
                    <th className="p-4">Transaction Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {circulation.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50/50 transition">
                      <td className="p-4 font-bold text-indigo-600 uppercase text-xs">{item.action_type}</td>
                      <td className="p-4 text-slate-700 font-semibold">{item.sender_first ? `${item.sender_first} ${item.sender_last}` : 'Admin'}</td>
                      <td className="p-4 text-slate-700 font-semibold">{item.receiver_first ? `${item.receiver_first} ${item.receiver_last}` : 'System'}</td>
                      <td className="p-4 font-black text-pink-600">{parseInt(item.coins_amount).toLocaleString()}</td>
                      <td className="p-4 text-slate-500 font-mono">{parseFloat(item.rate).toFixed(4)}</td>
                      <td className="p-4 text-slate-400">{new Date(item.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoinManagement;
